/**
 * <s-fireworks data-duration="3500"> — a short, one-time Diwali fireworks show over the page.
 * A few rockets rise and burst into sparks in festive colours for about data-duration ms, then the
 * layer fades out and removes itself. Never blocks taps or scrolling (pointer-events: none), is hidden
 * from screen readers, and does nothing for shoppers who prefer reduced motion.
 */
const FIREWORK_COLOURS = ['#f2a93b', '#ffd56b', '#ff5a3c', '#ff6fa8', '#ffffff', '#ffb347'];

class SFireworks extends HTMLElement {
  connectedCallback() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if (this.started) return;
    this.started = true;

    const duration = Number(this.dataset.duration) || 3500;
    const isSmall = window.matchMedia('(max-width: 767px)').matches;
    const bursts = isSmall ? 11 : 16;
    const sparks = isSmall ? 20 : 28;

    const layer = document.createElement('div');
    layer.setAttribute('aria-hidden', 'true');
    layer.style.cssText = 'position:fixed;inset:0;pointer-events:none;overflow:hidden;z-index:60;';
    this.appendChild(layer);

    const width = window.innerWidth;
    const height = window.innerHeight;
    const rise = 700;

    for (let b = 0; b < bursts; b += 1) {
      const x = width * (0.15 + Math.random() * 0.7);
      const y = height * (0.18 + Math.random() * 0.32);
      const start = (b / bursts) * Math.max(duration - 2100, 600) + Math.random() * 150;
      const colour = FIREWORK_COLOURS[Math.floor(Math.random() * FIREWORK_COLOURS.length)];
      const accent = FIREWORK_COLOURS[Math.floor(Math.random() * FIREWORK_COLOURS.length)];

      const rocket = document.createElement('span');
      rocket.style.cssText = `position:absolute;left:${x}px;top:0;width:3px;height:14px;border-radius:3px;background:linear-gradient(${colour},rgba(255,255,255,0));will-change:transform,opacity;`;
      layer.appendChild(rocket);
      rocket.animate(
        [
          { transform: `translate3d(0,${height + 20}px,0)`, opacity: 0 },
          { opacity: 1, offset: 0.1 },
          { transform: `translate3d(0,${y}px,0)`, opacity: 0.9 },
        ],
        { duration: rise, delay: start, easing: 'cubic-bezier(0.2, 0.7, 0.3, 1)', fill: 'both' }
      ).finished.then(() => rocket.remove()).catch(() => {});

      const radius = (isSmall ? 70 : 110) * (0.8 + Math.random() * 0.4);
      for (let s = 0; s < sparks; s += 1) {
        const angle = (s / sparks) * Math.PI * 2 + Math.random() * 0.2;
        const distance = radius * (0.7 + Math.random() * 0.3);
        const dx = Math.cos(angle) * distance;
        const dy = Math.sin(angle) * distance;
        const size = 3 + Math.random() * 2.5;
        const sparkColour = s % 3 === 0 ? accent : colour;
        const spark = document.createElement('span');
        spark.style.cssText = `position:absolute;left:${x}px;top:${y}px;width:${size}px;height:${size}px;margin:-${size / 2}px;border-radius:9999px;background:${sparkColour};box-shadow:0 0 6px ${sparkColour};will-change:transform,opacity;`;
        layer.appendChild(spark);
        spark.animate(
          [
            { transform: 'translate3d(0,0,0) scale(1)', opacity: 0 },
            { opacity: 1, offset: 0.05 },
            { transform: `translate3d(${dx * 0.85}px,${dy * 0.85}px,0) scale(1)`, opacity: 1, offset: 0.55 },
            { transform: `translate3d(${dx}px,${dy + 40}px,0) scale(0.4)`, opacity: 0 },
          ],
          { duration: 1100 + Math.random() * 300, delay: start + rise, easing: 'cubic-bezier(0.16, 1, 0.3, 1)', fill: 'both' }
        );
      }
    }

    const fadeOut = layer.animate([{ opacity: 1 }, { opacity: 0 }], {
      duration: 500,
      delay: duration,
      easing: 'cubic-bezier(0.16, 1, 0.3, 1)',
      fill: 'forwards',
    });
    fadeOut.finished.then(() => layer.remove()).catch(() => layer.remove());
  }
}

if (!customElements.get('s-fireworks')) customElements.define('s-fireworks', SFireworks);
